import React, { useRef } from 'react'
import cn from 'classnames'
import styles from './new-items-pill.module.scss'
import UserIcon from 'components/users/UserIcon'
import type { FeedNewItemsAuthor } from 'api/api'

interface NewItemsPillProps {
    count: number
    capped: boolean
    authors: FeedNewItemsAuthor[]
    source?: 'feed' | 'notifications' | 'ordinals'
    active?: boolean
}

interface PillContent {
    count: number
    capped: boolean
    authors: FeedNewItemsAuthor[]
}

// Feed new-items pill (docs/feed-new-items-pill.md): a separate affordance
// that grows out beside the filter capsule when unseen items wait above. The
// wrapper's width animates 0fr→1fr, which slides the capsule aside because
// their shared container is centered with translateX(-50%). Clicks fall
// through (pointer-events: none) to the container's refresh handler, same as
// the capsule.
export default function NewItemsPill({
    count,
    capped,
    authors,
    source = 'feed',
    active,
}: NewItemsPillProps) {
    const isActive = active ?? count > 0
    const pillTestId =
        source === 'notifications'
            ? 'notification-new-items-pill'
            : source === 'ordinals'
            ? 'ordinal-stream-updates-pill'
            : 'feed-new-items-pill'
    const countTestId =
        source === 'notifications'
            ? 'notification-new-items-count'
            : source === 'ordinals'
            ? 'ordinal-stream-updates-count'
            : 'feed-new-items-count'

    // Keep the last non-empty content through the exit animation so a refresh
    // (count → 0) collapses the pill without flashing "0 New" mid-close.
    const lastContentRef = useRef<PillContent | null>(null)
    if (isActive) {
        lastContentRef.current = { count, capped, authors }
    }
    const display = lastContentRef.current

    // Entrance styling is keyframe-driven (see the scss): animations fire on
    // class/mount without a pre-render "closed" frame, so no rAF two-phase
    // state is needed here — rAF is throttled or absent in backgrounded tabs.
    // The exit is transition-driven off the same class removal.
    if (!display) return null

    return (
        <div
            data-testid={pillTestId}
            className={cn(styles.newItemsPillWrap, isActive && styles.open)}
            aria-hidden={!isActive}
        >
            <div className={styles.clip}>
                <div className={styles.pill}>
                    {display.authors.length > 0 && (
                        <span className={styles.avatarPile}>
                            {/* Slot wrappers keep overlap/stagger selectors stable:
                                UserIcon's own DOM differs between the image and
                                initials render paths. */}
                            {display.authors.map(user => (
                                <span key={user.id} className={styles.avatarSlot}>
                                    <UserIcon
                                        user={user}
                                        contextStyles={styles.avatar}
                                        size="small"
                                    />
                                </span>
                            ))}
                        </span>
                    )}
                    <span data-testid={countTestId} className={styles.label}>
                        {display.count > 0 && <>{display.capped ? '20+' : display.count} </>}
                        {source === 'ordinals'
                            ? display.count === 1
                                ? 'Update'
                                : 'Updates'
                            : 'New'}
                    </span>
                    <i className={cn('fa fa-chevron-up', styles.chevron)} aria-hidden="true" />
                </div>
            </div>
        </div>
    )
}
